/* eslint-disable import/extensions */
import { OptionType } from '@oberoninternal/travelbase-ds/components/form/SelectInput';
import eachMonthOfInterval from 'date-fns/eachMonthOfInterval';
import endOfYear from 'date-fns/endOfYear';
import format from 'date-fns/format';
import startOfYear from 'date-fns/startOfYear';
import range from 'lodash.range';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import countriesListDE from '../constants/countries/de-DE.json';
import countriesListEN from '../constants/countries/en-GB.json';
import countriesListNL from '../constants/countries/nl-NL.json';

const getDefaultCountry = (locale: string, countryOptions: OptionType[]): string => {
    // In the en-GB.json file, there is no key of EN so we have to check for GB.
    if (locale === 'en') {
        return countryOptions.find(country => country.value === 'GB')?.value ?? '';
    }

    // In the nl-NL.json and de-DE.json files there are keys with NL and DE so we can just use the locale here.
    return countryOptions.find(country => country.value.toLowerCase() === locale)?.value ?? '';
};

const getCountriesList = (locale: string): Record<string, string> => {
    if (locale === 'nl') {
        return countriesListNL;
    }
    if (locale === 'de') {
        return countriesListDE;
    }

    return countriesListEN;
};

export const useCountryOptions = (): OptionType[] => {
    const { locale } = useIntl();

    return useMemo(() => {
        const countries: Record<string, string> = JSON.parse(JSON.stringify(getCountriesList(locale)));

        return Object.entries(countries).map(([key, val]) => ({
            value: key,
            label: val,
        }));
    }, [locale]);
};

export const useDefaultCountry = () => {
    const { locale } = useIntl();
    const countryOptions = useCountryOptions();
    return getDefaultCountry(locale, countryOptions);
};

export const useBirthdateOptions = () => {
    const { formatDate } = useIntl();

    const monthOptions = useMemo(
        () =>
            Object.values(eachMonthOfInterval({ start: startOfYear(new Date()), end: endOfYear(new Date()) })).map(
                date => ({
                    value: format(date, 'MM'),
                    label: formatDate(date, { month: 'long' }),
                })
            ),
        [formatDate]
    );

    const days = useMemo(
        () =>
            range(31).map(index => {
                const val = index + 1;
                return {
                    value: val.toLocaleString(undefined, { minimumIntegerDigits: 2 }),
                    label: String(val),
                };
            }),
        []
    );

    const now = new Date().getUTCFullYear();

    const yearOptions = useMemo(
        () =>
            range(now - 100, now).map(year => ({
                value: year.toString(),
                label: year.toString(),
            })),
        [now]
    );

    return { dayOptions: days, monthOptions, yearOptions };
};
